import React, { useEffect, useRef, useState } from "react";

interface LazyMapProps {
  src?: string;
  title: string;
  height?: number | string;
  className?: string;
  style?: React.CSSProperties;
  rounded?: boolean;
  address?: string;
  directionsUrl?: string;
}

const DEFAULT_DIRECTIONS =
  "https://www.google.com/maps/dir/?api=1&destination=1930+Marlton+Pike+East,+Cherry+Hill,+NJ+08003";

// Keyless Google Maps embed for Cherry Hill Recovery Center (resolved from
// https://maps.app.goo.gl/KKuWjPwP2zoCeH1z9). Keyless /maps/embed URLs are
// iframe-friendly, unlike plain /maps share links.
const DEFAULT_GOOGLE_EMBED_SRC =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d24440.0!2d-74.9633219!3d39.902184!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c1354275800c69%3A0xfd289f5ff51fc333!2sCherry%20Hill%20Recovery%20Center!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus";


const isBlockedGoogleUrl = (value?: string) => {
  if (!value) return false;
  try {
    const url = new URL(value);
    const hostname = url.hostname.replace(/^www\./, "");
    if (hostname !== "google.com" && hostname !== "maps.google.com") return false;
    // Keyless embeds (/maps/embed?pb=... and /maps/embed/v1) are allowed in
    // iframes; plain /maps share or search links are not.
    return !url.pathname.startsWith("/maps/embed");
  } catch {
    return false;
  }
};

const LazyMap = ({
  src,
  title,
  height = 380,
  className,
  style,
  rounded = true,
  address: _address,
  directionsUrl: _directionsUrl,
}: LazyMapProps) => {
  const h = typeof height === "number" ? `${height}px` : height;
  const containerRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!containerRef.current || inView) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          obs.disconnect();
        }
      },
      { rootMargin: "400px" }
    );
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [inView]);

  // Keyless Google Maps embeds are allowed; blocked Google URLs and missing
  // src fall back to the Google place embed.
  const mapSrc = !src || isBlockedGoogleUrl(src) ? DEFAULT_GOOGLE_EMBED_SRC : src;
  const directions = _directionsUrl || DEFAULT_DIRECTIONS;

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: "relative",
        height: h,
        width: "100%",
        borderRadius: rounded ? "8px" : undefined,
        overflow: "hidden",
        background: "#FAF4F0",
        ...style,
      }}
    >
      {inView && (
        <iframe
          title={title}
          src={mapSrc}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          tabIndex={-1}
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: 0,
            display: "block",
          }}
        />
      )}
      <a
        href={directions}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open directions to ${title}`}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          padding: "16px",
          textDecoration: "none",
        }}
      >
        <span
          style={{
            background: "rgba(15, 23, 42, 0.9)",
            color: "#fff",
            padding: "10px 16px",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: 600,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          Get Directions
        </span>
      </a>
    </div>
  );
};

export default LazyMap;
